// src/pages/admin/sandbox/AnimationSandboxTab.tsx
// Interactive testing surface for the token economy animation library.
// Lets admins trigger each animation in isolation or in composed form,
// preview against different SWA theme backgrounds, and tune duration.
//
// Assets are served from /tokens/* (public folder). Source of truth for
// the assets lives in team-tools/components/token-wallet/assets/.
//
// This used to live at /admin/animation-sandbox as its own page. It is now
// a tab inside /admin/sandbox.
import React, { useEffect, useRef, useState } from 'react';
import {
  PlayCircle, Square, Repeat, Coins, Sparkles, FlipHorizontal,
  CircleDot, Wand2, Image as ImageIcon, Layers,
  type LucideIcon,
} from 'lucide-react';

// ─── animation catalog ────────────────────────────────────────────────────────

type AnimSpec = {
  key: string;
  label: string;
  description: string;
  frameCount: number;
  defaultDurationMs: number;
  defaultLoop: boolean;
  endsEmpty: boolean;
  icon: LucideIcon;
};

const ANIMATIONS: AnimSpec[] = [
  { key: 'token-spin',       label: 'Token Spin',        description: 'Y-axis idle rotation. Loops in the wallet header.',     frameCount: 6, defaultDurationMs: 800, defaultLoop: true,  endsEmpty: false, icon: Repeat },
  { key: 'token-flip',       label: 'Token Flip',        description: 'X-axis tumble. One-shot on redemption confirmation.',   frameCount: 6, defaultDurationMs: 600, defaultLoop: false, endsEmpty: false, icon: FlipHorizontal },
  { key: 'token-spray',      label: 'Token Spray',       description: '5 coins burst outward. Layers behind the earn toast.',  frameCount: 6, defaultDurationMs: 600, defaultLoop: false, endsEmpty: true,  icon: Sparkles },
  { key: 'token-shine',      label: 'Token Shine',       description: 'Specular highlight sweeping across the coin face.',     frameCount: 6, defaultDurationMs: 1200, defaultLoop: true, endsEmpty: false, icon: Wand2 },
  { key: 'earn-pulse-halo',  label: 'Earn Pulse Halo',   description: 'Radial light burst behind the balance number.',        frameCount: 6, defaultDurationMs: 600, defaultLoop: false, endsEmpty: true,  icon: CircleDot },
];

// ─── theme presets ────────────────────────────────────────────────────────────

type ThemeKey = 'adminHq' | 'canonical' | 'rprsTeal' | 'walletModal';

const THEMES: Record<ThemeKey, { label: string; bg: string; border: string; muted: string }> = {
  adminHq:     { label: 'Admin HQ (dark)',     bg: '#0b0d10',                                            border: '#27303b', muted: '#a7b3c4' },
  canonical:   { label: 'Canonical (light)',   bg: '#f5f6f8',                                            border: '#d6dae0', muted: '#6b7280' },
  rprsTeal:    { label: 'RPRS dark teal',      bg: '#003D4C',                                            border: '#0a5666', muted: '#a7c8d1' },
  walletModal: { label: 'Wallet modal (white)', bg: '#ffffff',                                            border: '#e5e7eb', muted: '#6b7280' },
};

// ─── animation player ────────────────────────────────────────────────────────

type PlayerProps = {
  spec: AnimSpec;
  playId: number;
  loop: boolean;
  durationMs: number;
  size: number;
  style?: React.CSSProperties;
  onComplete?: () => void;
};

function AnimationPlayer({ spec, playId, loop, durationMs, size, style, onComplete }: PlayerProps) {
  const [frame, setFrame] = useState<number | null>(null);
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;

  useEffect(() => {
    if (playId === 0) {
      setFrame(null);
      return;
    }
    let i = 1;
    setFrame(1);
    const interval = setInterval(() => {
      i++;
      if (i > spec.frameCount) {
        if (loop) {
          i = 1;
          setFrame(i);
        } else {
          clearInterval(interval);
          if (spec.endsEmpty) setFrame(null);
          else setFrame(spec.frameCount);
          completeRef.current?.();
          return;
        }
      } else {
        setFrame(i);
      }
    }, durationMs / spec.frameCount);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playId, loop, durationMs]);

  if (frame === null) return null;
  return (
    <img
      src={`/tokens/animations/${spec.key}/frame-${frame}.png`}
      alt=""
      width={size}
      height={size}
      style={{ display: 'block', pointerEvents: 'none', ...style }}
    />
  );
}

// ─── tab body ────────────────────────────────────────────────────────────────

export default function AnimationSandboxTab() {
  const [themeKey, setThemeKey] = useState<ThemeKey>('adminHq');
  const [speed, setSpeed] = useState(1.0);
  const [size, setSize] = useState(256);

  const [plays, setPlays] = useState<Record<string, number>>({});
  const [loops, setLoops] = useState<Record<string, boolean>>({});

  const [toastPlayId, setToastPlayId] = useState(0);
  const [toastLayers, setToastLayers] = useState({
    illustration: true,
    spray: true,
    halo: false,
    count: true,
  });

  function trigger(spec: AnimSpec, asLoop: boolean) {
    setLoops(prev => ({ ...prev, [spec.key]: asLoop }));
    setPlays(prev => ({ ...prev, [spec.key]: (prev[spec.key] || 0) + 1 }));
  }
  function stop(spec: AnimSpec) {
    setLoops(prev => ({ ...prev, [spec.key]: false }));
    setPlays(prev => ({ ...prev, [spec.key]: 0 }));
  }

  function playToast() {
    setToastPlayId(prev => prev + 1);
  }

  const theme = THEMES[themeKey];

  return (
    <>
      {/* Header card */}
      <section className="card">
        <div className="hd">
          <div className="row">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontWeight: 760, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Layers size={18} /> Token Animation Sandbox
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                Trigger each animation against different SWA theme backgrounds. Tune speed and size before wiring into a real surface.
              </div>
            </div>
          </div>
        </div>
        <div className="bd">
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Background</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(Object.keys(THEMES) as ThemeKey[]).map(k => (
                  <button
                    key={k}
                    className={'btn' + (themeKey === k ? ' primary' : '')}
                    onClick={() => setThemeKey(k)}
                    style={{ fontSize: 12 }}
                  >
                    {THEMES[k].label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Speed</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="range"
                  min={0.25}
                  max={2.0}
                  step={0.05}
                  value={speed}
                  onChange={e => setSpeed(parseFloat(e.target.value))}
                  style={{ width: 160 }}
                />
                <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', minWidth: 44 }}>{speed.toFixed(2)}x</span>
              </div>
            </div>

            <div>
              <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Render size</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="range"
                  min={64}
                  max={512}
                  step={32}
                  value={size}
                  onChange={e => setSize(parseInt(e.target.value, 10))}
                  style={{ width: 160 }}
                />
                <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', minWidth: 44 }}>{size}px</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solo animations */}
      <section className="card">
        <div className="hd"><div className="row"><div style={{ fontWeight: 760 }}>Solo animations</div></div></div>
        <div className="bd">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {ANIMATIONS.map(spec => {
              const Icon = spec.icon;
              const active = (plays[spec.key] || 0) > 0;
              const looping = active && !!loops[spec.key];
              const adjustedDuration = spec.defaultDurationMs / speed;
              return (
                <div key={spec.key} style={{ border: `1px solid ${theme.border}`, borderRadius: 10, padding: 12, background: theme.bg, color: themeKey === 'canonical' || themeKey === 'walletModal' ? '#1f2937' : 'inherit' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <Icon size={16} />
                    <div style={{ fontWeight: 600 }}>{spec.label}</div>
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 12, minHeight: 32 }}>{spec.description}</div>

                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: size + 24, background: 'transparent', borderRadius: 8, marginBottom: 12 }}>
                    <AnimationPlayer
                      spec={spec}
                      playId={plays[spec.key] || 0}
                      loop={looping}
                      durationMs={adjustedDuration}
                      size={size}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button className="btn" onClick={() => trigger(spec, false)} title="Play once">
                      <PlayCircle size={14} /> Play
                    </button>
                    {spec.defaultLoop && (
                      looping ? (
                        <button className="btn primary" onClick={() => stop(spec)} title="Stop loop">
                          <Square size={14} /> Stop
                        </button>
                      ) : (
                        <button className="btn" onClick={() => trigger(spec, true)} title="Loop continuously">
                          <Repeat size={14} /> Loop
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Composed: Tokens Earned Toast */}
      <section className="card">
        <div className="hd">
          <div className="row">
            <div style={{ fontWeight: 760, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Coins size={18} /> Composed: Tokens Earned Toast
            </div>
            <div className="muted" style={{ fontSize: 12 }}>spray + illustration + count</div>
          </div>
        </div>
        <div className="bd">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14, fontSize: 13 }}>
            {(['illustration','spray','halo','count'] as const).map(layer => (
              <label key={layer} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={toastLayers[layer]}
                  onChange={e => setToastLayers(prev => ({ ...prev, [layer]: e.target.checked }))}
                />
                {layer}
              </label>
            ))}
          </div>

          <div style={{
            position: 'relative',
            background: theme.bg,
            color: themeKey === 'canonical' || themeKey === 'walletModal' ? '#1f2937' : '#e9eef5',
            borderRadius: 12,
            border: `1px solid ${theme.border}`,
            minHeight: 560,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden',
          }}>
            <ToastComposition
              playId={toastPlayId}
              durationMs={ANIMATIONS.find(a => a.key === 'token-spray')!.defaultDurationMs / speed}
              layers={toastLayers}
              theme={theme}
              themeKey={themeKey}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn primary" onClick={playToast}>
              <PlayCircle size={16} /> Play toast
            </button>
            <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>
              Click to fire the composed sequence. Layers above toggle what's visible.
            </span>
          </div>
        </div>
      </section>
    </>
  );
}

// ─── toast composition ───────────────────────────────────────────────────────

type ToastCompositionProps = {
  playId: number;
  durationMs: number;
  layers: { illustration: boolean; spray: boolean; halo: boolean; count: boolean };
  theme: { bg: string; border: string; muted: string };
  themeKey: ThemeKey;
};

const TOAST_ILLUSTRATION_URL  = '/tokens/illustrations/tokens-earned-toast-illustration.png';
const FALLBACK_ILLUSTRATION   = '/tokens/illustrations/redemption-success-illustration.png';

function ToastComposition({ playId, durationMs, layers, theme, themeKey }: ToastCompositionProps) {
  const spraySpec = ANIMATIONS.find(a => a.key === 'token-spray')!;
  const haloSpec  = ANIMATIONS.find(a => a.key === 'earn-pulse-halo')!;
  const [illustrationLoaded, setIllustrationLoaded] = useState(false);
  const [illustrationFailed, setIllustrationFailed] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.onload = () => { setIllustrationLoaded(true); setIllustrationFailed(false); };
    img.onerror = () => { setIllustrationLoaded(true); setIllustrationFailed(true); };
    img.src = TOAST_ILLUSTRATION_URL;
  }, []);

  const illustrationSrc = illustrationFailed ? FALLBACK_ILLUSTRATION : TOAST_ILLUSTRATION_URL;
  const illustrationLabel = illustrationFailed ? 'Using redemption-success placeholder (toast illustration not yet delivered)' : null;

  return (
    <div style={{
      position: 'relative',
      width: 512,
      height: 512,
    }}>
      {layers.halo && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, pointerEvents: 'none' }}>
          <AnimationPlayer
            spec={haloSpec}
            playId={playId}
            loop={false}
            durationMs={durationMs}
            size={512}
          />
        </div>
      )}

      {layers.illustration && illustrationLoaded && (
        <img
          src={illustrationSrc}
          alt=""
          width={512}
          height={512}
          style={{ position: 'absolute', inset: 0, zIndex: 2 }}
        />
      )}

      {layers.spray && (
        <div style={{
          position: 'absolute',
          left: 256, top: 64,
          width: 256, height: 256,
          zIndex: 3,
          pointerEvents: 'none',
        }}>
          <AnimationPlayer
            spec={spraySpec}
            playId={playId}
            loop={false}
            durationMs={durationMs}
            size={256}
          />
        </div>
      )}

      {layers.count && (
        <div style={{
          position: 'absolute',
          right: 24, bottom: 24,
          zIndex: 4,
          padding: '10px 16px',
          borderRadius: 999,
          background: themeKey === 'canonical' || themeKey === 'walletModal' ? '#003D4C' : 'rgba(255,255,255,.10)',
          color: themeKey === 'canonical' || themeKey === 'walletModal' ? 'white' : '#e9eef5',
          fontWeight: 700,
          fontSize: 18,
          fontVariantNumeric: 'tabular-nums',
          border: `1px solid ${theme.border}`,
          display: 'inline-flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ color: '#FFD24A', fontSize: 22 }}>◈</span>
          +5 tokens
        </div>
      )}

      {illustrationLabel && (
        <div style={{
          position: 'absolute',
          left: 12, top: 12,
          zIndex: 10,
          padding: '6px 10px',
          borderRadius: 6,
          background: 'rgba(255,209,102,0.15)',
          border: '1px solid rgba(255,209,102,0.4)',
          color: '#ffd166',
          fontSize: 11,
          maxWidth: 280,
        }}>
          <ImageIcon size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {illustrationLabel}
        </div>
      )}
    </div>
  );
}
